import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { actionClearCommandRes } from '@/redux/reducers/commandRes';
import { message } from 'antd';
import classNames from 'classnames';
import { RootState } from '@/redux/store';
import { useIsTakeOverBySelf } from '@/utils/hooks';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import { Method } from '@/fetch/core/util';
import { debounce, qidianClickLog, sgmReport } from '@/utils/util';
import { showModal } from '@/components/Modal';
import './index.scss';
import { TakeOverSource, TakeOverStatus } from '@/utils/type';
import { TakeOverProcessStatus } from '@/utils/enum';
import { useDispatch } from 'react-redux';

interface TakeOverProps {
  vehicleName: string;
}

const TakeOverSourceTextMap = new Map([
  [TakeOverSource.MONITOR, '监控'],
  [TakeOverSource.MINI_MONITOR, '小程序'],
  [TakeOverSource.REMOTE_JOYSTICK, '驾舱'],
  [TakeOverSource.WORKBENCH_MONITOR, '小哥工作台'],
]);

const TakeOver: React.FC<TakeOverProps> = ({ vehicleName }) => {
  // -------useSelector取值区域-------
  const LC_BUTTON_ENTER = useSelector(
    (state: RootState) => state.controlBtn?.LC_BUTTON_ENTER
  );
  const userName = useSelector(
    (state: RootState) => state.login?.userInfo?.userName
  );
  const cockpitNumber = useSelector(
    (state: RootState) => state.newCommon?.cockpitNumber
  );
  const isMqttConnected = useSelector(
    (state: RootState) => state.newCommon?.isMqttConnected
  );
  const takeOverStatus = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.takeOverStatus
  );
  const takeOverSource = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.takeOverSource
  );
  const takeOverUserName = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.takeOverUserName
  );
  const takeOverProcessStatus = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.takeOverProcessStatus
  );
  const takeOverTime = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.takeOverTime
  );
  const driveInMqtt1Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveInMqtt1Online
  );
  const driveInMqtt2Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveInMqtt2Online
  );
  const driveOutMqtt1Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveOutMqtt1Online
  );
  const driveOutMqtt2Online = useSelector(
    (state: RootState) => state.realtime?.realtimeData?.driveOutMqtt2Online
  );
  // -------useSelector取值区域-------

  const dispatch = useDispatch();

  const [isInit, setInit] = useState<boolean>(true); // 用来阻止方向盘快捷键初始化触发问题
  const takeOverBySelf = useIsTakeOverBySelf();
  const [takeOverByOther, setTakeOverByOther] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      clearTimeout(timer);
      setInit(false);
    }, 1000);
    return () => {
      clearTimeout(timer);
      dispatch(actionClearCommandRes());
    };
  }, []);
  useEffect(() => {
    if (
      (takeOverStatus === TakeOverStatus.TAKEOVER &&
        takeOverUserName !== userName) ||
      (takeOverUserName === userName &&
        takeOverSource !== TakeOverSource.REMOTE_JOYSTICK)
    ) {
      setTakeOverByOther(true);
    } else {
      setTakeOverByOther(false);
    }
  }, [takeOverStatus, takeOverUserName, userName]);
  // 获取当前时间戳（毫秒）
  const now = Date.now();
  // 计算时间差（毫秒）
  const timeDiff = takeOverTime ? now - takeOverTime : 0;

  const btnDisabled =
    (!driveInMqtt1Online && !driveInMqtt2Online) ||
    (!driveOutMqtt1Online && !driveOutMqtt2Online) ||
    takeOverByOther;
  const showTakeoverProcessBtn =
    takeOverProcessStatus === TakeOverProcessStatus.ENTER_TAKE_OVER_PROCESS &&
    takeOverUserName === userName;
  const takeoverProcessBtnClickable = timeDiff > 2000 && showTakeoverProcessBtn;
  const showTakeoverBtn =
    (takeOverProcessStatus === TakeOverProcessStatus.NULL &&
      !takeOverByOther) ||
    (takeOverProcessStatus === TakeOverProcessStatus.ENTER_TAKE_OVER_FAIL &&
      takeOverUserName === userName);
  const showExitProcessBtn =
    takeOverProcessStatus === TakeOverProcessStatus.EXIT_TAKE_OVER_PROCESS &&
    takeOverUserName === userName;
  const showExitBtn =
    (takeOverProcessStatus === TakeOverProcessStatus.ENTER_TAKE_OVER_SUCCESS ||
      takeOverProcessStatus === TakeOverProcessStatus.EXIT_TAKE_OVER_FAIL) &&
    takeOverUserName === userName;

  const timeoutHandledRef = useRef(false);

  useEffect(() => {
    const isTimeout = timeDiff > 20000 && takeoverProcessBtnClickable;

    if (isTimeout && !timeoutHandledRef.current) {
      timeoutHandledRef.current = true;
      showModal({
        title: '车辆接管超时',
        content: '车辆接管超时，已自动退出接管',
        footer: {
          showOk: true,
          okText: '关闭',
          okFunc: (cb) => cb(),
        },
      });
    }

    if (!showTakeoverProcessBtn) {
      timeoutHandledRef.current = false;
    }
  }, [timeDiff, takeoverProcessBtnClickable, showTakeoverProcessBtn]);

  const vehicleRecovery = useCallback(async () => {
    try {
      const requestOptions: any = {
        method: Method.POST,
        path: '/monitor/web/command/recovery',
        body: { vehicleName, timeStamp: Date.now() },
      };
      const res = await request(requestOptions);
      if (res.code === HttpStatusCode.Success) {
        message.success(`${vehicleName}恢复成功！`);
      } else {
        message.error(res.message || '网络请求错误！');
      }
    } catch (err) {
      console.error(err);
    }
  }, [vehicleName]);

  const handleTakeOverClick = useCallback(async () => {
    if (btnDisabled || !userName || !cockpitNumber || !vehicleName) {
      return;
    }

    const commandParams = {
      cockpitNumber,
      vehicleName,
      cockpitUserName: userName,
    };

    try {
      const res = await request({
        path: '/monitor/web/drive/command/send/enter-take-over',
        method: Method.POST,
        body: commandParams,
      });
      if (res.code === HttpStatusCode.Success) {
        message.success('已发送接管指令');
      } else {
        message.error(`接管失败，原因：${res.message}`);
      }
    } catch (error: any) {
      sgmReport(`${vehicleName}/handleTakeOver/error`, error);
      throw error;
    }
  }, [btnDisabled, userName, cockpitNumber, vehicleName]);

  const exitTakeOverClick = useCallback(async () => {
    if (btnDisabled || !userName || !cockpitNumber || !vehicleName) {
      return;
    }
    try {
      const commandParams = {
        cockpitNumber,
        vehicleName,
        cockpitUserName: userName,
      };
      const res = await request({
        path: '/monitor/web/drive/command/send/exit-take-over',
        method: Method.POST,
        body: commandParams,
        keepalive: true,
      });
      if (res.code === HttpStatusCode.Success) {
        message.success('已发送退出接管指令');
      } else {
        message.error(`退出接管失败，原因：${res.message}`);
      }
    } catch (error) {
      console.error('退出接管失败:', error);
      sgmReport(`${vehicleName}/exitTakeOver/error`, error);
    }
  }, [cockpitNumber, vehicleName, userName, btnDisabled]);
  const debouncedHandleTakeOver = useMemo(
    () => debounce(handleTakeOverClick, 500),
    [handleTakeOverClick]
  );
  const debouncedVehicleRecovery = useMemo(
    () => debounce(vehicleRecovery, 500),
    [vehicleRecovery]
  );
  const debouncedExitTakeOver = useMemo(
    () => debounce(exitTakeOverClick, 500),
    [exitTakeOverClick]
  );
  useEffect(() => {
    if (LC_BUTTON_ENTER && !isInit) {
      if (btnDisabled) {
        return;
      }
      if (showTakeoverBtn) {
        debouncedHandleTakeOver(); // 使用稳定的防抖函数
        qidianClickLog('app_singleview|takeOverVehicle', { enterKey: true });
      } else if (takeOverStatus === TakeOverStatus.TEMPORARY) {
        debouncedVehicleRecovery();
      } else if (
        takeoverProcessBtnClickable ||
        showExitBtn ||
        showExitProcessBtn
      ) {
        debouncedExitTakeOver();
        qidianClickLog('app_singleview|exitTakeover', { enterKey: true });
      }
    }
  }, [
    LC_BUTTON_ENTER,
    takeOverByOther,
    isInit,
    btnDisabled,
    showTakeoverBtn,
    takeoverProcessBtnClickable,
    showExitBtn,
    showExitProcessBtn,
    takeOverStatus,
  ]);
  useEffect(() => {
    const cb = (event: any) => {
      event.preventDefault();
      if (!takeOverBySelf) {
        return;
      }
      exitTakeOverClick();
      event.returnValue = '';
    };
    process.env.NODE_ENV === 'production' &&
      window.addEventListener('beforeunload', cb);
    process.env.NODE_ENV === 'production' &&
      window.addEventListener('popstate', cb, false);
    return () => {
      window.removeEventListener('beforeunload', cb);
      window.removeEventListener('popstate', cb);
    };
  }, [takeOverBySelf]);

  if (!isMqttConnected) {
    return (
      <div className="take-over-btn disabled">
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">建立连接中...</i>
        </div>
      </div>
    );
  }
  // 接管处理中按钮
  if (showTakeoverProcessBtn) {
    return takeoverProcessBtnClickable ? (
      <div
        className={classNames('take-over-btn', { disable: false })}
        clstag="app_singleview|takeOverMiddle"
        onClick={debouncedExitTakeOver}
      >
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">请求中</i>
          <i>再次点击退出接管</i>
        </div>
      </div>
    ) : (
      <div
        className={classNames('take-over-btn', { disable: true })}
        clstag="app_singleview|takeOverMiddle"
      >
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">请求中</i>
          <i>请求接管中，请勿离开页面</i>
        </div>
      </div>
    );
  }
  // 接管按钮
  if (showTakeoverBtn) {
    return (
      <div
        className={classNames('take-over-btn', { disable: btnDisabled })}
        onClick={debouncedHandleTakeOver}
        clstag="app_singleview|takeOverVehicle"
      >
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">接管车辆</i>
          <i>点击方向盘大按钮也可接管车辆</i>
        </div>
      </div>
    );
  }

  // 退出接管按钮
  if (showExitBtn) {
    return (
      <div
        className={classNames('take-over-btn', { disable: btnDisabled })}
        clstag="app_singleview|exitTakeover"
        onClick={debouncedExitTakeOver}
      >
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">退出接管</i>
          <i>点击方向盘大按钮也可退出接管</i>
        </div>
      </div>
    );
  }

  // 退出处理中按钮
  if (showExitProcessBtn) {
    return (
      <div
        className={classNames('take-over-btn', { disable: btnDisabled })}
        clstag="app_singleview|exitTakeoverMiddle"
        onClick={debouncedExitTakeOver}
      >
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">退出中...</i>
          <i>支持多次下发退出指令</i>
        </div>
      </div>
    );
  }
  // 被他人接管按钮
  if (takeOverByOther) {
    return (
      <div className="take-over-btn disabled">
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">车辆已被接管</i>
          <i>
            {takeOverUserName}于{TakeOverSourceTextMap.get(takeOverSource)}
            接管中...
          </i>
        </div>
      </div>
    );
  }

  if (takeOverStatus === TakeOverStatus.TEMPORARY) {
    return (
      <div
        className={classNames('take-over-btn', { disable: btnDisabled })}
        onClick={debounce(vehicleRecovery, 500)}
      >
        <div className="btn">
          <img src={require('@/assets/images/monitor/bg-btn.png')} alt="" />
          <i className="btn-text">接管车辆</i>
          <i>
            {takeOverUserName}于{TakeOverSourceTextMap.get(takeOverSource)}
            临时停车中
          </i>
        </div>
      </div>
    );
  }
  return null;
};

export default React.memo(TakeOver);
